import { Divider, Loader } from 'components';
import { ScrollContainer } from 'components/ScrollContainer/ScrollContainer';
import { COMMUNITY_ROUTES, ROUTES } from 'configs';
import { VIEW_MEMBER } from 'constants/sidebars';
import { useInfoPopup } from 'hooks';
import useChatLazyLoading from 'hooks/useChatLazyLoading';
import i18n from 'i18next';
import { isEmpty, noop } from 'lodash/fp';
import { useCommunityNamesById } from 'pages/PushNotification/util';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { DefaultAvatar } from 'static';
import {
  doClearFiles,
  doGetConversation,
  doGetPresignedUrl,
  doSendMessage,
  doUpdateConversation,
  doUpdateReadCount,
} from 'store/actionCreators/chat';
import {
  doClearCommunity,
  doRequestCommunityForChat,
} from 'store/actionCreators/currentCommunity';
import { openModal } from 'store/actionCreators/modal';
import { doUpdateTotalChatCount } from 'store/actionCreators/profile';
import { ChatUser, Conversation, Messages } from 'store/reducers/chat';
import { getAuthUser, getSettings } from 'store/selectors/auth';
import {
  deleteChatGroupLoading,
  getChat,
  getChatUserLoading,
  getConversationLoading,
  getCountUpdateLoader,
  getIsOldChat,
  sendMessageLoading,
} from 'store/selectors/chat';
import {
  getCurrentMembers,
  getIsMenuOpen,
} from 'store/selectors/currentCommunity';
import { CommunityUser, ModalTypes } from 'types';
import { getDate } from 'utils';
import { download, getProfileNameCapital } from 'utils/common';
import { getDateForEngLocation, getTimeWithA } from 'utils/dates';

import ChatInput from '../ChatInput';
import DateSeparator from './DateSeparator';
import { FORMFIELDS, useCreateChatForm } from './form';
import Header from './Header';
import Message from './Message';
import Placeholder from './Placeholder';
import {
  ChatLayout,
  DivChat,
  Layout,
  MessageContainer,
  MessageWrapper,
  PlaceholderContainer,
  RedirectWrapper,
  Wrapper,
} from './styled';

interface ChatProps {
  openUserList: boolean;
  toggleSidebar: noop;
  count: number | null;
  openCreateChatPopUp: () => void;
  talkId: string;
  peerId: string;
  communityId?: string;
}
const AVAILABLE_SIZE = 5 * 1024;
export const validImageSize = (imageFile) =>
  imageFile && Math.round(imageFile.size / 1024) < AVAILABLE_SIZE;
const Chat: FC<ChatProps> = ({
  openUserList,
  toggleSidebar,
  count,
  openCreateChatPopUp,
  talkId,
  peerId,
  communityId,
}) => {
  const { addToast } = useToasts();
  const deleteGroupLoading = useSelector(deleteChatGroupLoading);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSidebarOpen = useSelector(getIsMenuOpen);
  const { showInfoPopup } = useInfoPopup();
  const dispatch = useDispatch();
  const settings = useSelector(getSettings);
  const chatUserLoading = useSelector(getChatUserLoading);
  const chatList = useSelector(getChat);
  const countUpdateLoader = useSelector(getCountUpdateLoader);
  const chatListLoading = useSelector(getConversationLoading);
  const sendMessageLoader = useSelector(sendMessageLoading);
  const user = useSelector(getAuthUser);
  const [chats, setChats] = useState<Conversation | null>(null);
  const flag = useRef<boolean>(false);
  const [selectedPeerUser, setSelectedPeerUser] = useState<ChatUser | null>(
    null,
  );
  const isOldChat = useSelector(getIsOldChat);
  const getChats = (data) => {
    flag.current = true;
    dispatch(
      doUpdateConversation({
        userId: user.userId,
        chatId: talkId,
        receiverId: peerId,
        limit: data.limit,
        page: data.page,
      }),
    );
  };

  useEffect(() => {
    if (!(count > 0) || countUpdateLoader) {
      return;
    }
    const chatId = talkId || chats?.id || null;
    dispatch(doUpdateReadCount.success({ count, chatId }));
    dispatch(doUpdateTotalChatCount.success({ count }));
  }, [count, countUpdateLoader, talkId, chats?.id, dispatch]);

  const { listRef } = useChatLazyLoading({
    isLoading: chatListLoading,
    loadMessages: getChats,
    pageSize: 20,
    scrollOffset: 1300,
    totalMessages: chats?.messages?.length || 0,
    hasNextPage: chats?.messages && chats.messages.length < chats.messageCount,
  });

  const hasMessages = chats?.messages && chats.messages.length > 0;
  const members = useSelector(getCurrentMembers);
  let member: CommunityUser;

  let idDisabled = false;
  if (selectedPeerUser) {
    idDisabled = !members.find(
      (member) => member.id === selectedPeerUser.userId,
    );
  }
  if (peerId) {
    member = members.find((member) => member.id === peerId);
    if (!member) {
      idDisabled = true;
      navigate(`${ROUTES.COMMUNITY}/${communityId}/${COMMUNITY_ROUTES.CHAT}`);
    }
  }

  const defaultValues = {
    message: '',
    attachments: [],
  };

  useEffect(() => {
    if (!communityId && chatList?.communityId?.length) {
      dispatch(
        doRequestCommunityForChat({ communityId: chatList?.communityId[0] }),
      );
    }
    return () => {
      if (!communityId) {
        dispatch(doClearCommunity());
      }
    };
  }, [communityId, chatList?.communityId]);

  useEffect(() => {
    setChats(chatList);
    if (!chatList?.isGroupChat) {
      setSelectedPeerUser(
        chatList?.peerUsers.find((u) => u.userId !== user.userId),
      );
    }
  }, [chatList]);

  useEffect(() => {
    if (peerId || talkId) {
      clearErrors();
      reset(defaultValues);
      //todo
      // getConversationFromDB(talkId).then((res) => {
      // if (res) {
      //   dispatch(doSetConversation.success(res));
      //   setChats(res);
      //   if (!res?.isGroupChat) {
      //     setSelectedPeerUser(
      //       res?.peerUsers.find((user) => user.userId == user.userId),
      //     );
      //   }
      // }
      // if (!res) {
      //   dispatch(
      //     doGetConversation({
      //       userId: user.userId,
      //       chatId: talkId,
      //       receiverId: peerId,
      //       count,
      //     }),
      //   );
      // }
      // });
      // .catch((err) => {});
      setChats(null);
      setSelectedPeerUser(null);
      dispatch(
        doGetConversation({
          userId: user.userId,
          chatId: talkId,
          receiverId: peerId,
          count,
          page: 1,
          limit: 20,
        }),
      );
    }
  }, [talkId, peerId]);

  const openViewMemberSidebar = useCallback(
    (userId: string) => {
      if (members?.length === 0) {
        return;
      }
      const disabled = !members.find((member) => member.id === userId);
      const user: CommunityUser | undefined = userId
        ? members?.find((member) => member.id === userId)
        : undefined;
      const peerUser = findPeerUser(userId);
      if (!chats?.isGroupChat && disabled && userId) {
        addToast(t('chat.popover.message'), {
          appearance: 'info',
          autoDismiss: true,
        });
        return;
      }
      if (!peerUser?.active && chats?.isGroupChat && userId) {
        addToast(t('chat.popover.message'), {
          appearance: 'info',
          autoDismiss: true,
        });
        return;
      }
      if (!userId) {
        dispatch(
          openModal({
            type: ModalTypes.ABOUT_CHAT_GROUP,
            placement: 'right',
            data: {
              chat: {
                id: talkId,
                chatName: chatList?.chatName,
                description: chatList?.description,
                logo: chatList?.logo || null,
                admin: chatList?.admin,
              },
              createdAt: chatList?.createdAt,
              members: chats?.peerUsers,
              communityId: communityId || chatList.communityId,
            },
          }),
        );
      } else {
        dispatch(
          openModal({
            type: VIEW_MEMBER,
            placement: 'right',
            data: user || member,
          }),
        );
      }
    },
    [communityId, members, chatList, chats, talkId],
  );

  const {
    errors,
    register,
    handleSubmit,
    clearErrors,
    control,
    setValue,
    trigger: triggerFormValidation,
    watchedValues,
    attachmentsFieldProps,
    setError,
    reset,
  } = useCreateChatForm(defaultValues);

  const onSendMessage = async (docType: 'document' | 'photo') => {
    flag.current = false;
    handleSubmit(async (data) => {
      const mediaDetails = data[FORMFIELDS.ATTACHMENTS];
      const messageContent = data[FORMFIELDS.MESSAGE];
      if (!messageContent && (!mediaDetails || mediaDetails.length === 0)) {
        setError(FORMFIELDS.MESSAGE, {
          type: 'required',
          message: t('errors.required'),
        });
        return;
      }

      const type: 'text' | 'image' | 'file' =
        mediaDetails && mediaDetails.length > 0
          ? docType === 'photo'
            ? 'image'
            : 'file'
          : 'text';

      const sendMessage = async (mediaUrls: string[] = []) => {
        try {
          const messagePayload = {
            userId: user.userId,
            chatId: talkId || chats?.id || null,
            content: messageContent,
            type,
            communityId:
              communityId ||
              chatList?.messages?.at(-1)?.communityId ||
              chatList?.communityId[0],
            isGroup: !!chats?.isGroupChat,
            mediaUrls,
            ...(!chats?.isGroupChat && {
              receiverId: selectedPeerUser?.userId || peerId,
            }),
            onSuccess: () => {
              setValue(FORMFIELDS.MESSAGE, '');
              setValue(FORMFIELDS.ATTACHMENTS, []);
              dispatch(doClearFiles());
            },
          };
          dispatch(doSendMessage.trigger(messagePayload));
        } catch (err) {
          showInfoPopup({
            title: 'Something went wrong',
            message: 'Please try again',
            type: 'error',
            loop: false,
          });
          console.error('Error sending message:', err);
        }
      };
      if (mediaDetails?.length > 0) {
        try {
          await dispatch(
            doGetPresignedUrl.trigger({
              chatId: talkId || chats?.id || '',
              userId: user.userId,
              mediaDetail: mediaDetails,
              dispatch,
              success: (urls: string[]) => {
                sendMessage(urls);
              },
            }),
          );
        } catch (err) {
          console.error('Error fetching presigned URLs:', err);
          showInfoPopup({
            title: 'Something went wrong',
            message: 'Unable to fetch presigned URLs. Please try again',
            type: 'error',
            loop: false,
          });
        }
      } else {
        sendMessage();
      }
    })();
  };

  const onSubmit = async (type: 'document' | 'photo') => {
    const isValid = await triggerFormValidation();
    if (isValid) onSendMessage(type);
  };

  const scrollToElement = useCallback((id: string) => {
    if (listRef.current) {
      const targetElement = document.getElementById(id);
      if (targetElement && targetElement.nextElementSibling) {
        const nextSibling = targetElement.nextElementSibling as HTMLElement;
        const container = listRef.current;
        if (container) {
          const targetOffset =
            nextSibling.getBoundingClientRect().top -
            container.getBoundingClientRect().top;
          const containerScrollTop = container.scrollTop;

          const stickyOffset = 40;
          container.scrollTop =
            containerScrollTop + targetOffset - stickyOffset;
        }
      }
    }
  }, []);

  const findPeerUser = useCallback(
    (id: string) => {
      return chats?.peerUsers.find((peer) => peer.userId === id);
    },
    [chats],
  );
  const isEveryoneRead = useMemo(() => {
    return (message: Messages) => {
      if (!chats?.peerUsers?.length || !message?.readStatus) return false;

      const otherPeerUsers = chats.peerUsers.filter(
        (peerUser) => peerUser.userId !== user?.userId && peerUser.active,
      );

      if (chats?.isGroupChat && otherPeerUsers.length === 0) return false;

      if (
        message.readStatus.length === 0 &&
        otherPeerUsers.length === 0 &&
        !chats?.isGroupChat
      )
        return false;

      if (otherPeerUsers.length === 0) return true;
      return otherPeerUsers.every((peerUser) =>
        message.readStatus.some((status) => status.userId === peerUser.userId),
      );
    };
  }, [chats?.peerUsers, user?.userId]);

  const urlUser = useMemo(
    () => (id: string) => {
      const peerUser = findPeerUser(id);
      if (!peerUser?.active) {
        return DefaultAvatar;
      }
      return peerUser
        ? peerUser.logo || peerUser.smallLogo
        : user?.logo || user?.smallLogo;
    },
    [chats, user],
  );

  const getUserName = useMemo(
    () => (id: string) => {
      const peerUser = findPeerUser(id);
      if (!peerUser?.active) {
        return t('chat.popover.title');
      }
      return peerUser
        ? peerUser.firstName || peerUser.profileName
        : user?.firstName || user?.profileName;
    },
    [chats, user],
  );

  const senderLastName = useMemo(
    () => (id: string) => {
      const peerUser = findPeerUser(id);
      // console.log('peerUser', peerUser);
      if (!peerUser?.active) {
        return '';
      }
      return peerUser
        ? peerUser.lastName || peerUser.profileName
        : user?.lastName || user?.profileName;
    },
    [chats, user],
  );

  const communityNames = useCommunityNamesById(chatList?.communityId || []);

  const description = `${communityNames}  ${
    chatList?.description ? ' • ' + chatList.description : ''
  }`;

  const chatListComponent: JSX.Element | JSX.Element[] = useMemo(() => {
    if (!chats?.messages.length) {
      return;
    }

    return chats.messages.map((message: Messages, index) => {
      const sameAuthor =
        index > 0 && message?.senderId === chats.messages[index - 1]?.senderId;
      const sameDay =
        index > 0 &&
        getDate(message?.createdAt)?.toLowerCase() ===
          getDate(chats.messages[index - 1]?.createdAt)?.toLowerCase();
      const time = getTimeWithA(message?.createdAt, settings.timeFormat);

      const date =
        i18n.language === 'en'
          ? getDateForEngLocation(message?.createdAt)
          : getDate(message?.createdAt);

      return (
        <React.Fragment key={message.id}>
          {(!sameDay || index === 0) && (
            <DateSeparator date={date} handleScroll={scrollToElement} />
          )}
          <MessageContainer>
            <MessageWrapper
              reverse={message.senderId !== user.userId}
              data-item-key={message.id}
            >
              <Message
                isGroup={chatList?.isGroupChat}
                openViewMemberSidebar={openViewMemberSidebar}
                userId={user.userId}
                isOpen={openUserList}
                urlUser={urlUser(message.senderId)}
                senderFirstName={getUserName(message.senderId)}
                senderLastName={senderLastName(message.senderId)}
                content={message.content}
                date={message.createdAt}
                sameAuthor={sameAuthor}
                time={time}
                sameDay={sameDay}
                mediaType={message.type}
                mediaUrl={message?.mediaUrl}
                senderId={message.senderId}
                readStatus={message.readStatus}
                isEveryoneRead={isEveryoneRead(message)}
              />
            </MessageWrapper>
          </MessageContainer>
        </React.Fragment>
      );
    });
  }, [
    chats?.messages,
    settings.timeFormat,
    i18n.language,
    scrollToElement,
    openViewMemberSidebar,
    openUserList,
    urlUser,
    getUserName,
    senderLastName,
    user.userId,
  ]);

  const handleDocumentsAttach = async (data) => {
    try {
      const newFiles = await Promise.all(
        data
          .filter(
            (i) =>
              !watchedValues[FORMFIELDS.ATTACHMENTS].some(
                (f) => f.name === i.name,
              ),
          )
          .map((i) => download(i.url, i.name, i.mime)),
      );

      return attachmentsFieldProps.onChange([
        ...watchedValues[FORMFIELDS.ATTACHMENTS],
        ...newFiles,
      ]);
    } catch {
      addToast(t('errors.downloadFail'), {
        appearance: 'error',
        autoDismiss: true,
      });
      throw new Error('document download failed');
    }
  };
  const handleRemoveDocument = (document: any) => {
    attachmentsFieldProps.onChange(
      watchedValues[FORMFIELDS.ATTACHMENTS].filter(
        (i) => i.name !== document.name,
      ),
    );
  };

  return (
    <Layout isOpen={openUserList}>
      {deleteGroupLoading && <Loader type="main" />}
      <Header
        t={t}
        userId={user.userId}
        isSidebarOpen={isSidebarOpen}
        openUserList={openUserList}
        toggleSidebar={toggleSidebar}
        selectedPeerUser={selectedPeerUser}
        communityId={communityId || chatList?.communityId[0]}
        peerId={peerId}
        talkId={talkId}
        isGroup={chatList?.isGroupChat}
        groupName={chatList?.chatName}
        member={member}
        members={chats?.peerUsers}
        description={chatList?.description}
        logo={chatList?.logo}
        headerDesc={description}
        createdAt={chatList?.createdAt}
        admin={chatList?.admin}
        openViewMemberSidebar={openViewMemberSidebar}
        loading={chatListLoading || chatUserLoading}
      />
      <Divider customMargin="0px" />
      {!isEmpty(talkId) || !isEmpty(peerId) ? (
        <ChatLayout>
          <DivChat>
            {chatListLoading || chatUserLoading ? (
              <Loader size="60px" type={null} />
            ) : hasMessages ? (
              <ScrollContainer isOpen={openUserList} outerDiv={listRef}>
                {chatListComponent}
              </ScrollContainer>
            ) : null}
            {!isOldChat && (
              <Wrapper>
                {t('chat.emptyStatus.sendFirstMessage', {
                  recipient: chatList?.isGroupChat
                    ? chatList?.chatName
                    : getProfileNameCapital({
                        firstName:
                          selectedPeerUser?.firstName ||
                          member?.profile?.firstName,
                        lastName:
                          selectedPeerUser?.lastName ||
                          member?.profile?.lastName,
                      }) ||
                      member?.profile?.profileName ||
                      member?.profile?.email,
                })}
              </Wrapper>
            )}
          </DivChat>
          <RedirectWrapper>
            <Divider customMargin="0px" />
            <ChatInput
              watchedValues={watchedValues}
              clearErrors={clearErrors}
              attachmentsFieldProps={attachmentsFieldProps}
              sendMessageLoader={sendMessageLoader || chatListLoading}
              isOpen={openUserList}
              onSubmit={onSubmit}
              control={control}
              deleteGroupLoading={deleteGroupLoading}
              errors={errors}
              register={register}
              isActiveSend={
                watchedValues[FORMFIELDS.MESSAGE].length > 0 ||
                watchedValues[FORMFIELDS.ATTACHMENTS].length > 0
              }
              onDocumentsAttach={handleDocumentsAttach}
              communityId={communityId}
              onRemoveDocument={handleRemoveDocument}
              isDisabled={!chats?.isGroupChat && idDisabled}
            />
          </RedirectWrapper>
        </ChatLayout>
      ) : (
        <PlaceholderContainer>
          <Placeholder
            imageUrl=""
            text={t('chat.emptyStatus.noChatSelected.firstLine')}
            linkText={t('chat.emptyStatus.noChatSelected.LinkText')}
            openCreateChatPopUp={openCreateChatPopUp}
          />
        </PlaceholderContainer>
      )}
    </Layout>
  );
};

export default Chat;
